import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { PERSONAS, DEBTORS, INTEGRATIONS, EVENT_LOG } from '@/data/seed';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [personaId, setPersonaId] = useState('manager');
  const [language, setLanguage] = useState('EN');
  const dir = language === 'AR' ? 'rtl' : 'ltr';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [debtors, setDebtors] = useState(DEBTORS);
  const [integrations, setIntegrations] = useState(INTEGRATIONS);
  const [events, setEvents] = useState(EVENT_LOG);
  const [notifications, setNotifications] = useState([
    { id: 1, kind: 'success', title: 'Payment received', body: 'CASE-10412 — SAR 1,500 cleared', time: '2m' },
    { id: 2, kind: 'warning', title: 'SLA at risk', body: '14 cases approaching breach', time: '12m' },
    { id: 3, kind: 'info', title: 'New portfolio', body: 'Tamam BNPL PRT-2026-016 — 3,120 accounts', time: '38m' },
  ]);

  // Apply dir on <html> so the entire layout mirrors when AR is picked.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', dir);
      document.documentElement.setAttribute('lang', language === 'AR' ? 'ar' : 'en');
    }
  }, [dir, language]);

  const persona = useMemo(() => PERSONAS.find((p) => p.id === personaId), [personaId]);

  const pushEvent = useCallback((evt) => {
    setEvents((prev) => [{ eventId: `EVT-2026-${(2100 + prev.length).toString().padStart(6, '0')}`, timestamp: new Date().toISOString(), latencyMs: 200 + Math.round(Math.random() * 400), correlationId: `CORR-${9000 + prev.length}`, ...evt }, ...prev].slice(0, 60));
  }, []);

  const updateDebtor = useCallback((id, patch) => {
    setDebtors((prev) => prev.map((d) => (d.caseId === id ? { ...d, ...patch } : d)));
  }, []);

  const toggleIntegration = useCallback((id) => {
    setIntegrations((prev) => prev.map((it) => (it.id === id ? { ...it, status: it.status === 'connected' ? 'failed' : 'connected', lastEvent: 'just now' } : it)));
  }, []);

  const pushNotification = useCallback((n) => {
    setNotifications((prev) => [{ id: Date.now(), time: 'now', ...n }, ...prev].slice(0, 10));
  }, []);

  const value = {
    personaId, setPersonaId, persona,
    language, setLanguage, dir,
    sidebarCollapsed, setSidebarCollapsed,
    debtors, updateDebtor,
    integrations, toggleIntegration,
    events, pushEvent,
    notifications, pushNotification,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
